import IndexController from "./router/index.controller";
import SignOutController from "./router/signout.controller";

/* @ngInject */
export default function angularRoutes(
  $stateProvider,
  $locationProvider,
  $urlRouterProvider
) {
  /**
   * Sign up routing
   */
  $stateProvider.state("signup", {
    url: "/signup",
    component: "signUpPage",
  });

  /**
   * Log in routing
   */
  $stateProvider.state("login", {
    url: "/login",
    component: "logInPage",
  });

  /**
   * Access from Mobile routing
   */
  $stateProvider.state("accessFromMobile", {
    url: "/access-from-mobile",
    component: "accessFromMobile",
  });

  /**
   * Reset password routing
   */
  $stateProvider.state("resetPassword", {
    url: "/reset-password?token",
    component: "resetPasswordPage",
  });

  /**
   * Sign out routing
   */
  $stateProvider.state("signout", {
    url: "/signout",
    controllerAs: "$ctrl",
    controller: SignOutController,
    data: { requiresLogin: true },
  });

  /**
   * Integrate with Smart Vault
   */
  $stateProvider.state("integrate", {
    url: "/smart-vault",
    component: "integrationCardSmartvault",
    data: { requiresLogin: true },
    resolve: {
      user: /* @ngInject */ (SecurityManager) => SecurityManager.getUser(),
    },
  });

  /**
   * Create organizations
   */
  $stateProvider.state("createOrganizations", {
    url: "/create-organizations",
    component: "createOrganizationsPage",
    data: { requiresLogin: true },
    resolve: {
      user: /* @ngInject */ (SecurityManager) => SecurityManager.getUser(),
    },
  });

  /**
   * Index routing
   */
  $stateProvider
    .state("index", {
      url: "/",
      controllerAs: "$ctrl",
      controller: IndexController,
      data: { requiresLogin: true },
      resolve: {
        user: /* @ngInject */ (SecurityManager) => SecurityManager.getUser(),
        smartVault: /* @ngInject */ (SmartVault) => SmartVault.integrated(),
      },
    })
    .state("createProposal", {
      url: "/create-proposal?:fid&:cid",
      component: "createProposal",
      data: { requiresLogin: true },
    });

  /**
   * App routing
   */
  $stateProvider
    .state("app", {
      abstract: true,
      url: "/:organization?:cid",
      templateProvider: /* @ngInject */ (
        $stateParams,
        $templateCache,
        user
      ) => {
        if (user.hasOrganization($stateParams.organization))
          return $templateCache.get("templates/router/app.html");
        else return $templateCache.get("templates/router/not-found.html");
      },
      data: { requiresLogin: true },
      resolve: {
        user: /* @ngInject */ (SecurityManager) => SecurityManager.getUser(),
        organization: /* @ngInject */ (
          $window,
          $timeout,
          $rootScope,
          user,
          $stateParams,
          $state,
          LocaleService
        ) => {
          $rootScope.organization = user.getOrganization(
            $stateParams.organization
          );
          if (!$rootScope.organization) return $state.go("index");

          LocaleService.setLanguage(
            $rootScope.organization.settings.appLanguage
          );
          LocaleService.setTimezone($rootScope.organization.settings.timezone);

          if (process.env.ENV === "prod") {
            $timeout(() => {
              $window.hj("tagRecording", [user.email]);
            }, 1000);
          }

          return $rootScope.organization;
        },
      },
    })
    .state("app.dashboard", {
      url: "",
      component: "dashboardPage",
    })
    .state("app.templateSelector", {
      url: "/templates",
      component: "templateSelectorPage",
    })
    .state("app.quoteEditor", {
      url: "/quote/:quoteId/editor",
      component: "quoteEditorPage",
      resolve: {
        // Inject organization for sequential resolve of parent and child states
        quote: /* @ngInject */ (organization, Quote, $stateParams) =>
          Quote.get($stateParams.quoteId),
      },
    })
    .state("app.quoteViewer", {
      url: "/quote/:quoteId/viewer?version",
      component: "showQuote",
      resolve: {
        quote: /* @ngInject */ (organization, Quote, $stateParams) =>
          Quote.get($stateParams.quoteId),
      },
    })
    .state("app.contacts", {
      url: "/contacts",
      component: "libraryContactsPage",
    })
    .state("app.tariffsLibrary", {
      url: "/library/tariffs",
      component: "libraryTariffsPage",
    })
    .state("app.costsLibrary", {
      url: "/library/costs",
      component: "libraryCostsPage",
    })
    .state("app.organization", {
      abstract: true,
      url: "/organization",
      component: "organizationSettingsPage",
      data: {
        requiresOwner: true,
      },
    })
    .state("app.organization.profile", {
      url: "/profile",
      component: "organizationProfile",
    })
    .state("app.organization.team", {
      url: "/team",
      component: "settingsTeam",
    })
    .state("app.organization.settings", {
      url: "/settings",
      component: "settings",
    })
    .state("app.organization.branding", {
      url: "/branding",
      component: "brandingSettings",
    })
    // .state("app.organization.customDomain", {
    //   url: "/custom-domain",
    //   component: "customDomainSettings",
    // })
    .state("app.organization.customFields", {
      url: "/custom-fields",
      component: "settingsCustomFields",
    })
    .state("app.integration", {
      abstract: true,
      url: "/integration",
      component: "integrationPage",
      data: {
        requiresOwner: true,
      },
    })
    .state("app.profile", {
      url: "/profile",
      component: "profilePage",
    });

  const generateRedirect = (state) => {
    return [
      "$window",
      "$state",
      "SecurityManager",
      async ($window, $state, SecurityManager) => {
        const user = await SecurityManager.getUser();
        $window.location =
          $state.href(state, { organization: user.defaultOrganization.slug }) +
          $window.location.search;
      },
    ];
  };

  $urlRouterProvider
    .when("/create-proposal", generateRedirect("createProposal"))
    .when("/dashboard", generateRedirect("app.dashboard"))
    .when("/templates", generateRedirect("app.templateSelector"))
    .when("/contacts", generateRedirect("app.contacts"))
    .when("/library/tariffs", generateRedirect("app.tariffsLibrary"))
    .when("/library/costs", generateRedirect("app.costsLibrary"))
    .when("/organization", generateRedirect("app.organization"))
    .when("/organization-profile", generateRedirect("app.organization.profile"))

    .when("/team", generateRedirect("app.organization.team"))
    .when("/settings", generateRedirect("app.organization.settings"))
    .when("/branding", generateRedirect("app.organization.branding"))
    // .when("/custom-domain", generateRedirect("app.organization.customDomain"))

    .when("/integration", generateRedirect("app.integration"))
    .when("/profile", generateRedirect("app.profile"));

  /**
   * if the path doesn't match any of the urls redirects to index
   */
  $urlRouterProvider.otherwise("/");

  /**
   * Enable HTML5 Mode
   */
  $locationProvider.html5Mode(true);
}
